import { Checkbox, Input, Label } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { HealthieTemplatePicker } from "./HealthieTemplatePicker";
import { TemplateFormDialogContentsSectionForm } from "./types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@tellescope/tanstack-query";
import { VALID_VIDEO_INTEGRATIONS } from "./constants";

export interface IntegrationsSectionProps {
  form: TemplateFormDialogContentsSectionForm<
    | "sendIcsEmail"
    | "videoIntegration"
    | "dontAutoSyncPatientToHealthie"
    | "matchToHealthieTemplate"
    | "healthieTemplateId"
    | "healthieInsuranceBillingEnabled"
    | "dontSyncToCanvas"
    | "canvasCoding.system"
    | "canvasCoding.code"
    | "canvasCoding.display"
    | "canvasReasonCoding.system"
    | "canvasReasonCoding.code"
    | "canvasReasonCoding.display"
    | "dontSyncToElation"
    | "athenaDepartmentId"
    | "athenaTypeId"
    | "athenaBookingTypeId"
    | "generateAthenaTelehealthLink"
    | "createAndBookAthenaSlot"
  >;
}

export const IntegrationsSection = ({ form }: IntegrationsSectionProps) => {
  const { t } = useTranslation("calendar");
  const { selectedOrganization } = useAuth();
  const [matchMode, setMatchMode] = useState<"title" | "template">(
    form.values.matchToHealthieTemplate && !form.values.healthieTemplateId ? "title" : "template"
  );

  return (
    <section className="space-y-6">
      <span className="flex gap-2 items-center">
        <Checkbox
          checked={form.values.sendIcsEmail}
          onCheckedChange={(checked) => form.setFieldValue("sendIcsEmail", !!checked)}
          id="sendIcsEmail"
        />
        <Label htmlFor="sendIcsEmail">{t("Send emails with ICS attachments to contact attendees")}</Label>
      </span>
      <div className="space-y-2">
        <Label htmlFor="videoIntegration">{t("Video Meeting Provider")}</Label>
        <Select {...form.getCustomFieldProps("videoIntegration")}>
          <Select.Trigger id="videoIntegration" className="w-full">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {VALID_VIDEO_INTEGRATIONS.map((val) => (
              <Select.Item key={val} value={val}>
                {val === "No Integration" ? t("None") : val}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
      {!!selectedOrganization?.hasConnectedHealthie && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium border-b pb-2">{t("Healthie Integration")}</h3>
          <span className="flex gap-2 items-center">
            <Checkbox
              checked={form.values.dontAutoSyncPatientToHealthie}
              onCheckedChange={(checked) => form.setFieldValue("dontAutoSyncPatientToHealthie", !!checked)}
              id="dontAutoSyncPatientToHealthie"
            />
            <Label htmlFor="dontAutoSyncPatientToHealthie">
              {t("Don't autosync Patient to Healthie when event is created")}
            </Label>
          </span>
          <div className="space-y-4">
            <span className="flex gap-2 items-center">
              <Checkbox
                checked={form.values.matchToHealthieTemplate}
                onCheckedChange={(checked) => {
                  form.setFieldValue("matchToHealthieTemplate", !!checked);
                  if (!checked) form.setFieldValue("healthieTemplateId", "");
                }}
                id="matchToHealthieTemplate"
              />
              <Label htmlFor="matchToHealthieTemplate">{t("Match to Healthie (Healthie → Tellescope)")}</Label>
            </span>
            {!!form.values.matchToHealthieTemplate && (
              <div className="space-y-4 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="healthieMatchMode">{t("Match by")}</Label>
                  <Select
                    value={matchMode}
                    onValueChange={(value) => {
                      if (value === "title") form.setFieldValue("healthieTemplateId", "");
                      setMatchMode(value === "template" ? "template" : "title");
                    }}
                  >
                    <Select.Trigger id="healthieMatchMode" className="w-full">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.Item value="title">{t("Healthie title")}</Select.Item>
                      <Select.Item value="template">{t("A specific Healthie template")}</Select.Item>
                    </Select.Content>
                  </Select>
                </div>
                <div className={matchMode === "template" ? "space-y-2" : "hidden"}>
                  <Label htmlFor="healthieTemplateId">{t("Healthie Template")}</Label>
                  <HealthieTemplatePicker
                    id="healthieTemplateId"
                    value={form.values.healthieTemplateId}
                    onChange={(healthieTemplateId) => form.setFieldValue("healthieTemplateId", healthieTemplateId)}
                  />
                </div>
              </div>
            )}
          </div>
          <span className="flex gap-2 items-center">
            <Checkbox
              checked={form.values.healthieInsuranceBillingEnabled}
              onCheckedChange={(checked) => form.setFieldValue("healthieInsuranceBillingEnabled", !!checked)}
              id="healthieInsuranceBillingEnabled"
            />
            <Label htmlFor="healthieInsuranceBillingEnabled">{t("Enable Insurance Billing in Healthie")}</Label>
          </span>
        </div>
      )}
      {!!selectedOrganization?.hasConnectedCanvas && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium border-b pb-2">{t("Canvas Integration")}</h3>
          <span className="flex gap-2 items-center">
            <Checkbox
              checked={form.values.dontSyncToCanvas}
              onCheckedChange={(checked) => form.setFieldValue("dontSyncToCanvas", !!checked)}
              id="dontSyncToCanvas"
            />
            <Label htmlFor="dontSyncToCanvas">{t("Don't Sync to Canvas")}</Label>
          </span>
          <div className="space-y-4 pt-2">
            <Label>{t("Canvas Appointment Type Coding")}</Label>
            <span className="grid grid-cols-3 gap-3">
              <Input {...form.getFieldProps("canvasCoding.system")} placeholder={t("System")} />
              <Input {...form.getFieldProps("canvasCoding.code")} placeholder={t("Code")} />
              <Input {...form.getFieldProps("canvasCoding.display")} placeholder={t("Display")} />
            </span>
          </div>
          <div className="space-y-4 pt-2">
            <Label>{t("Canvas Appointment Reason Coding")}</Label>
            <span className="grid grid-cols-3 gap-3">
              <Input {...form.getFieldProps("canvasReasonCoding.system")} placeholder={t("System")} />
              <Input {...form.getFieldProps("canvasReasonCoding.code")} placeholder={t("Code")} />
              <Input {...form.getFieldProps("canvasReasonCoding.display")} placeholder={t("Display")} />
            </span>
          </div>
        </div>
      )}
      {!!selectedOrganization?.hasConnectedAthena && (
        <div className="space-y-6">
          <h3 className="text-sm font-medium border-b pb-2">{t("athenahealth Integration")}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="athenaDepartmentId">{t("Department ID")}</Label>
              <Input
                id="athenaDepartmentId"
                {...form.getFieldProps("athenaDepartmentId")}
                placeholder={t("athenahealth Department ID")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="athenaTypeId">{t("Appointment Slot Type ID")}</Label>
              <Input
                id="athenaTypeId"
                {...form.getFieldProps("athenaTypeId")}
                placeholder={t("athenahealth Appointment Slot Type ID")}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="athenaBookingTypeId">{t("Booked Appointment Type ID")}</Label>
              <Input
                id="athenaBookingTypeId"
                {...form.getFieldProps("athenaBookingTypeId")}
                placeholder={t("athenahealth Booked Appointment Type ID (override slot type)")}
              />
            </div>
            <div className="space-y-4">
              <span className="flex gap-2 items-center">
                <Checkbox
                  checked={form.values.generateAthenaTelehealthLink}
                  onCheckedChange={(checked) => form.setFieldValue("generateAthenaTelehealthLink", !!checked)}
                  id="generateAthenaTelehealthLink"
                />
                <Label htmlFor="generateAthenaTelehealthLink">{t("Generate athenahealth telehealth link")}</Label>
              </span>
              <span className="flex gap-2 items-center">
                <Checkbox
                  checked={form.values.createAndBookAthenaSlot}
                  onCheckedChange={(checked) => form.setFieldValue("createAndBookAthenaSlot", !!checked)}
                  id="createAndBookAthenaSlot"
                />
                <Label htmlFor="createAndBookAthenaSlot">{t("Sync manually-created events to athenahealth")}</Label>
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
